import { CardContentFooterActions } from '@/components/Card'
import { CardSubtitle, CardTitle } from '@/components/Card'
import { CardContentDecoration } from '@/components/Card'
import { CardContent } from '@/components/Card'
import { Icon } from '@/components/Icon'
import { SectionHeadline } from '@/components/SectionHeadline'
import { useId, type ReactNode } from 'react'
import { Card } from '@/components/Card'
import type ConsultationManagerViewModel from '../../ConsultationManagerViewModel'
import { TranscriptDownloadDropdownContainer } from '../TranscriptDownloadDropdown'
import { formatDate } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { PageSection } from '@/features/jobs/details/components/PageLayout'

interface TranscriptDownloadSectionProps {
  headline: string
  title: string
  description: string
  variation: 'default' | 'empty'
  children: ReactNode
  testId?: string
}

/**
 * Appointment Section
 */
export const TranscriptDownloadSection = ({
  headline,
  title,
  description,
  variation,
  children,
  testId,
}: TranscriptDownloadSectionProps) => {
  const headlineId = useId()
  const titleText = variation === 'empty' ? 'No transcript yet' : title
  const descriptionText =
    variation === 'empty'
      ? 'Completed transcripts will appear here after the call with your Pro.'
      : description

  return (
    <PageSection testId={testId}>
      <SectionHeadline id={headlineId}>{headline}</SectionHeadline>
      <Card>
        <CardContent>
          <CardContentDecoration>
            <Icon name={'file'} variant="surface-background" />
          </CardContentDecoration>
          <CardTitle>{titleText}</CardTitle>
          <CardSubtitle>{descriptionText}</CardSubtitle>
          {variation !== 'empty' && (
            <CardContentFooterActions>{children}</CardContentFooterActions>
          )}
        </CardContent>
      </Card>
    </PageSection>
  )
}

interface TranscriptDownloadSectionContainerProps {
  vm: ConsultationManagerViewModel
}

/**
 * Transcript Download Section Container
 */
export const TranscriptDownloadSectionContainer = observer(
  function TranscriptDownloadSectionContainer({
    vm,
  }: TranscriptDownloadSectionContainerProps) {
    const isTranscriptAvailable = vm.transcriptVms.length > 0
    const dateGenerated = isTranscriptAvailable
      ? vm.transcriptVms[0].doc.dateCreated
      : new Date()

    return (
      <TranscriptDownloadSection
        headline="Your transcript"
        title={
          vm.transcriptVms.length === 1
            ? 'Transcript available'
            : `${vm.transcriptVms.length} transcripts available`
        }
        description={`Generated ${formatDate(dateGenerated, 'MMM d, yyyy')} `}
        variation={isTranscriptAvailable ? 'default' : 'empty'}
      >
        <TranscriptDownloadDropdownContainer vm={vm} />
      </TranscriptDownloadSection>
    )
  },
)
